export type GridAction =
  | { type: 'move'; delta: number }
  | { type: 'line'; delta: number }
  | { type: 'field'; field: 'chinese' | 'pinyin' | 'definition' }
  | { type: 'level'; level: number }
  | { type: 'awaitLevel' }
  | { type: 'cancelLevel' }
  | { type: 'find' }
  | { type: 'confirm' }
  | { type: 'photo'; delta: number }
  | { type: 'spread'; delta: number }
  | { type: 'merge'; before: boolean }
  | { type: 'delete' }
  | { type: 'add'; before: boolean }
  | { type: 'reorder'; delta: number }
  | { type: 'lineBreak' }
  | { type: 'undo'; redo: boolean }
  | { type: 'select'; delta: number }
  | { type: 'clearSelection' }
  | { type: 'none' };

// Grid mode. Field-jump keys name a destination on the focused Phrase, which is what replaced
// `Enter`-to-open in ADR 0042 — the panel is always in view, so there is nothing to open.
//
// `p` is pinyin, not Photo: pinyin is reached constantly and Photos only on multi-Photo Spreads,
// so Photo cycling moved to `,`/`.`. `d` is definition, not delete: issue 15's delete keeps
// `Backspace`/`Delete`, which is the natural key regardless.
export function gridKeyAction(
  event: Pick<KeyboardEvent, 'key' | 'metaKey' | 'ctrlKey' | 'shiftKey'> & { altKey?: boolean },
  state: { awaitingLevel?: boolean } = {},
): GridAction {
  // `a` is a prefix, not an action: it arms the next keypress. A bare digit does nothing, so a
  // stray number key cannot silently retag a Phrase — assigning a level is two deliberate presses,
  // which is what makes it strong enough to vet (ADR 0042).
  if (state.awaitingLevel) {
    if (/^[0-9]$/.test(event.key) && !event.metaKey && !event.ctrlKey) {
      return { type: 'level', level: event.key === '0' ? 10 : Number(event.key) };
    }
    return { type: 'cancelLevel' };
  }
  if ((event.metaKey || event.ctrlKey) && event.key.toLowerCase() === 'z') return { type: 'undo', redo: event.shiftKey };
  if (event.shiftKey && event.key.startsWith('Arrow')) {
    if (event.key === 'ArrowLeft' || event.key === 'ArrowUp') return { type: 'select', delta: -1 };
    return { type: 'select', delta: 1 };
  }
  if (event.key === 'Escape') return { type: 'clearSelection' };
  if ((event.metaKey || event.ctrlKey) && event.key === 'Enter') return { type: 'confirm' };
  if (event.metaKey || event.ctrlKey) return { type: 'none' };
  // Shift reverses the direction of both, so the pair reads as one idea rather than four keys.
  if (event.key === 'm') return { type: 'merge', before: false };
  if (event.key === 'M') return { type: 'merge', before: true };
  if (event.key === 'Backspace' || event.key === 'Delete') return { type: 'delete' };
  if (event.key === 'n') return { type: 'add', before: false };
  if (event.key === 'N') return { type: 'add', before: true };
  if (event.altKey && event.key === 'ArrowLeft') return { type: 'reorder', delta: -1 };
  if (event.altKey && event.key === 'ArrowRight') return { type: 'reorder', delta: 1 };
  // `/` rather than `\`: unshifted on US and UK layouts, next to `.` which already cycles Photos,
  // and reachable without AltGr on the German and French layouts where `\` is not.
  if (event.key === '/') return { type: 'lineBreak' };
  if (event.key === 'ArrowLeft') return { type: 'move', delta: -1 };
  if (event.key === 'ArrowRight') return { type: 'move', delta: 1 };
  if (event.key === 'ArrowUp') return { type: 'line', delta: -1 };
  if (event.key === 'ArrowDown') return { type: 'line', delta: 1 };
  if (event.key === 'c' || event.key === 'C' || event.key === 'Enter') {
    return { type: 'field', field: 'chinese' };
  }
  if (event.key === 'p' || event.key === 'P') return { type: 'field', field: 'pinyin' };
  if (event.key === 'f' || event.key === 'F') return { type: 'find' };
  if (event.key === 'd' || event.key === 'D') return { type: 'field', field: 'definition' };
  if (event.key === 'a' || event.key === 'A') return { type: 'awaitLevel' };
  if (event.key === ',') return { type: 'photo', delta: -1 };
  if (event.key === '.') return { type: 'photo', delta: 1 };
  if (event.key === '[') return { type: 'spread', delta: -1 };
  if (event.key === ']') return { type: 'spread', delta: 1 };
  return { type: 'none' };
}

// Leaving the panel is the commit (ADR 0042). Reaching a field with c/p/d is a decision to look at
// that Phrase, so it vets on the way out whether or not a value changed — unlike traversal, which
// ADR 0040 rightly refused to let vet anything. The one exception is unchanged: if the sync trigger
// re-derived a Dictionary value, the admin has not seen it, so the Phrase stays unvetted.
export function shouldVetPhraseCommit(
  original: { chinese: string; pinyin: string; definition: string | null; assessment_level: number | null },
  draft: { chinese: string; pinyin: string; definition: string | null; assessment_level: number | null },
  reread: { definition: string | null; assessment_level: number | null } | null,
) {
  if (draft.chinese === original.chinese && draft.pinyin === original.pinyin) return true;
  return Boolean(reread && reread.definition === draft.definition && reread.assessment_level === draft.assessment_level);
}

// Which pinyin pill is selected, and what picking one composes.
//
// CC-CEDICT either knows the whole Phrase — one row of complete readings — or it does not, in
// which case each character contributes a row and the pinyin is composed syllable by syllable.
// Collapsing those two cases into one flat list is what made pills never look selected: a
// whole-Phrase pinyin can never equal a single character's reading, so 上海人 showed
// `shàng hǎi rén` on its card with nothing marked in the panel.

export function isSyllablePicked(
  perCharacter: boolean,
  pinyin: string,
  row: number,
  option: string,
): boolean {
  if (!perCharacter) return pinyin === option;
  return (pinyin ? pinyin.split(' ') : [])[row] === option;
}

export const UNSET_SYLLABLE = '?';

export function composePinyin(
  perCharacter: boolean,
  chinese: string,
  pinyin: string,
  row: number,
  option: string,
): string {
  if (!perCharacter) return option;
  const segments = pinyin ? pinyin.split(' ') : [];
  // Unpicked syllables hold a visible placeholder rather than an empty string. A space-joined
  // string cannot represent a blank leading syllable — trimming it shifts every later syllable
  // down a character, so picking the third reading before the first silently made it the first.
  const next = [...chinese].map((_, index) => segments[index] || UNSET_SYLLABLE);
  next[row] = option;
  return next.join(' ');
}

// A pinyin still carrying a placeholder is incomplete, and must not pass the gate that a merely
// non-empty string would. Published Characters are a positional 1:1 split of pinyin against
// characters, so `shàng ? rén` would publish `?` as a Character's reading.
export function isPinyinComplete(pinyin: string | null | undefined): boolean {
  if (!pinyin || !pinyin.trim()) return false;
  return !pinyin.split(' ').includes(UNSET_SYLLABLE);
}
